import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';

export interface Country {
  id: string;
  name: string;
  code: string;
}

export interface CountryResponse {
  content: Country[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface CountryRequest {
  pageNumber: number;
  pageSize: number;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private API_BASE: string = inject(API_BASE_URL);
  private apiUrl = `${this.API_BASE}/country`;
  private countriesSubject = new BehaviorSubject<Country[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private totalElementsSubject = new BehaviorSubject<number>(0);
  private http = inject(HttpClient);

  private currentPage = 0;
  private currentPageSize = 10;

  getCountries(pageNumber = 0, pageSize = 10): Observable<CountryResponse> {
    this.loadingSubject.next(true);

    this.currentPage = pageNumber;
    this.currentPageSize = pageSize;

    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<CountryResponse>(this.apiUrl, { params }).pipe(
      tap(response => {
        this.countriesSubject.next(response.content);
        this.totalElementsSubject.next(response.totalElements);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        console.error('Error fetching countries:', error);
        return throwError(() => error);
      })
    );
  }

  getCountriesObservable(): Observable<Country[]> {
    return this.countriesSubject.asObservable();
  }

  getLoadingState(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  getTotalElements(): Observable<number> {
    return this.totalElementsSubject.asObservable();
  }

  getAllCountries(): Country[] {
    return this.countriesSubject.value;
  }

  getCountryById(id: string): Observable<Country> {
    return this.http.get<Country>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        console.error(`Error fetching country with id ${id}:`, error);
        return throwError(() => error);
      })
    );
  }

  addCountry(country: Omit<Country, 'id'>): Observable<ApiResponse<Country>> {
    return this.http.post<Country>(this.apiUrl, country).pipe(
      map(newCountry => ({
        data: newCountry,
        message: `Страна "${country.name}" успешно добавлена`,
        success: true,
      })),
      tap(() => {
        this.refreshCurrentPage();
      }),
      catchError(error => {
        console.error('Error adding country:', error);
        return throwError(() => ({
          message: 'Не удалось добавить страну. Попробуйте еще раз.',
          success: false,
          error,
        }));
      })
    );
  }

  updateCountry(id: string, updates: Partial<Country>): Observable<ApiResponse<Country>> {
    return this.http.put<Country>(`${this.apiUrl}/${id}`, updates).pipe(
      map(updatedCountry => ({
        data: updatedCountry,
        message: `Страна "${updates.name || 'обновлена'}" успешно обновлена`,
        success: true,
      })),
      tap(() => {
        this.refreshCurrentPage();
      }),
      catchError(error => {
        console.error(`Error updating country with id ${id}:`, error);
        return throwError(() => ({
          message: 'Не удалось обновить страну. Попробуйте еще раз.',
          success: false,
          error,
        }));
      })
    );
  }

  deleteCountry(id: string, countryName?: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<boolean>(`${this.apiUrl}/${id}`).pipe(
      map(result => ({
        data: result,
        message: `Страна "${countryName || 'удалена'}" успешно удалена`,
        success: true,
      })),
      tap(() => {
        this.refreshCurrentPage();
      }),
      catchError(error => {
        console.error(`Error deleting country with id ${id}:`, error);
        return throwError(() => ({
          message: 'Не удалось удалить страну. Попробуйте еще раз.',
          success: false,
          error,
        }));
      })
    );
  }

  searchCountries(query: string, pageNumber = 0, pageSize = 10): Observable<CountryResponse> {
    this.loadingSubject.next(true);

    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('search', query);

    return this.http.get<CountryResponse>(`${this.apiUrl}/search`, { params }).pipe(
      tap(response => {
        this.countriesSubject.next(response.content);
        this.totalElementsSubject.next(response.totalElements);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        console.error('Error searching countries:', error);
        return throwError(() => error);
      })
    );
  }

  private refreshCurrentPage(): void {
    this.getCountries(this.currentPage, this.currentPageSize).subscribe();
  }
}
